import "./fonts";
import { el, copyToClipboard } from "./components/dom";
import { iconCopy } from "./components/icons";
import { mountToastHost, reassertStickyToasts } from "./components/toast";
import { renderHelpRail } from "./components/helpRail";
import { CryptoWorkerClient } from "./lib/workerClient";
import { renderKeygenPanel } from "./components/keygenPanel";
import { renderEncryptStringPanel } from "./components/encryptStringPanel";
import { renderDecryptStringPanel } from "./components/decryptStringPanel";
import { renderEncryptFilePanel } from "./components/encryptFilePanel";
import { renderDecryptFilePanel } from "./components/decryptFilePanel";
import { renderSecurityPanel } from "./components/securityPanel";
import { renderOverviewPanel } from "./components/overviewPanel";
import { renderServiceApiPanel } from "./components/serviceApiPanel";
import { renderIntegrationPanel } from "./components/integrationPanel";
import { renderSdkPanel } from "./components/sdkPanel";
import { requestApiToken, type ApiConfig } from "./lib/api";
import { getApiBaseUrl } from "./lib/env";
import { apiTokenState, setApiToken, onApiTokenChange } from "./lib/apiToken";
import { knownKeys, activeKeyHex, onKeyRegistryChange, rememberKey, setActiveKeyHex } from "./lib/keyRegistry";
import { humanizeGeneric } from "./lib/humanizeError";
import { showToast } from "./components/toast";
import "./style.css";

const worker = new CryptoWorkerClient();

// Baked in per environment via VITE_API_BASE_URL (.env.development/.staging/.production).
// There's no Settings screen, so this isn't user-editable at runtime.
const apiConfig: ApiConfig = { baseUrl: getApiBaseUrl() };

export type TabId =
  | "overview"
  | "keygen"
  | "encrypt-string"
  | "decrypt-string"
  | "encrypt-file"
  | "decrypt-file"
  | "integration"
  | "sdk"
  | "service-api"
  | "security";

interface TabDef {
  id: TabId;
  label: string;
  idx: string;
}
interface TabGroup {
  label: string;
  tabs: TabDef[];
}

const tabGroups: TabGroup[] = [
  { label: "start", tabs: [{ id: "overview", label: "Overview", idx: "00" }] },
  { label: "keys", tabs: [{ id: "keygen", label: "Generate keypair", idx: "01" }] },
  {
    label: "strings",
    tabs: [
      { id: "encrypt-string", label: "Encrypt a string", idx: "02" },
      { id: "decrypt-string", label: "Decrypt a string", idx: "03" },
    ],
  },
  {
    label: "files",
    tabs: [
      { id: "encrypt-file", label: "Encrypt a file", idx: "04" },
      { id: "decrypt-file", label: "Decrypt a file", idx: "05" },
    ],
  },
  {
    label: "docs",
    tabs: [
      { id: "integration", label: "Integration guide", idx: "06" },
      { id: "sdk", label: "SDK", idx: "07" },
    ],
  },
  {
    label: "system",
    tabs: [
      { id: "service-api", label: "Service API", idx: "08" },
      { id: "security", label: "Security", idx: "09" },
    ],
  },
];

const ALL_TAB_IDS: TabId[] = tabGroups.flatMap((g) => g.tabs.map((t) => t.id));

// No server routes, but tabs are still bookmarkable/linkable via the URL hash
// (the SDK README links straight to #integration). Just history + a re-render,
// no page load.
function tabFromHash(): TabId | null {
  const id = window.location.hash.slice(1);
  return (ALL_TAB_IDS as string[]).includes(id) ? (id as TabId) : null;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function fromHex(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

// Forward-declared (like setKnownPublicKey below) since panels need to
// trigger a tab switch, but selectTab isn't defined until mount() runs.
let goToTab: (id: TabId) => void = () => {};

// Requests a fresh self-service API token and stores it. This is the one
// action that stands in for "sign in" - used by the sidebar token block
// and by any panel that needs relay access but has no token yet.
async function requestToken(): Promise<void> {
  try {
    const { api_key, expires_at } = await requestApiToken(apiConfig.baseUrl);
    setApiToken(api_key, expires_at);
    showToast("API token issued.", "success");
  } catch (e) {
    showToast(humanizeGeneric(e, "Couldn't get an API token."), "error");
  }
}

function renderPanel(id: TabId): HTMLElement {
  switch (id) {
    case "overview":
      return renderOverviewPanel((tab) => goToTab(tab));
    case "service-api":
      return renderServiceApiPanel(apiConfig, (tab) => goToTab(tab));
    case "keygen":
      return renderKeygenPanel(worker, (publicKey) => setKnownPublicKey(publicKey));
    case "encrypt-string":
      return renderEncryptStringPanel(worker);
    case "decrypt-string":
      return renderDecryptStringPanel(worker);
    case "encrypt-file":
      return renderEncryptFilePanel(worker, apiConfig, requestToken);
    case "decrypt-file":
      return renderDecryptFilePanel(worker, apiConfig, requestToken);
    case "integration":
      return renderIntegrationPanel((tab) => goToTab(tab));
    case "sdk":
      return renderSdkPanel((tab) => goToTab(tab));
    case "security":
      return renderSecurityPanel((tab) => goToTab(tab));
  }
}

// Right-rail public-keys card, rebuilt fresh on every tab switch. Safe to
// throw away and recreate because the real state (known keys, which is
// active) lives in lib/keyRegistry.ts, not in this DOM subtree. The
// onKeyRegistryChange subscription is scoped to this instance though, so
// callers must call dispose() before discarding it or the old listener
// keeps re-rendering a detached card.
//
// Clicking a non-active row switches the worker's active key and it's
// immediately usable for decryption - no passphrase re-entry, since the
// private key never left the worker.
let setKnownPublicKey: (publicKey: Uint8Array) => void = () => {};

function buildKeysCard(): { element: HTMLElement; dispose: () => void } {
  const card = el("div", { class: "keyblock keys-card" }, []);

  function activate(hex: string) {
    worker
      .setActiveKey(fromHex(hex))
      .then(() => setActiveKeyHex(hex))
      .catch((e) => {
        showToast(humanizeGeneric(e, "Couldn't switch to that key."), "error");
      });
  }

  function buildRow(hex: string): HTMLElement {
    const isActive = hex === activeKeyHex;
    let expanded = false;

    // #pubkey-out always tags whichever row is active, so tests can wait
    // on "#pubkey-out has content" without caring which key it is.
    const valueRow = el(
      "button",
      { type: "button", class: "value", ...(isActive ? { id: "pubkey-out" } : {}) },
      [`${hex.slice(0, 8)}…${hex.slice(-8)}`],
    );
    // title always holds the full key - the hover tooltip, and what tests
    // read to identify a key regardless of expand/collapse state.
    valueRow.title = hex;

    function renderValue() {
      valueRow.textContent = expanded ? hex : `${hex.slice(0, 8)}…${hex.slice(-8)}`;
    }

    // Clicking the active row toggles expand/collapse; clicking any other
    // row activates it instead - the one you click is the one that's used.
    valueRow.addEventListener("click", () => {
      if (hex === activeKeyHex) {
        expanded = !expanded;
        renderValue();
      } else {
        activate(hex);
      }
    });

    const copyBtn = el("button", { type: "button", class: "copy-btn", "aria-label": "Copy public key" }, [
      iconCopy(),
    ]);
    copyBtn.addEventListener("click", async (e) => {
      e.stopPropagation(); // don't also fire valueRow's click handler
      const ok = await copyToClipboard(hex);
      copyBtn.setAttribute("aria-label", ok ? "Copied" : "Copy failed");
      setTimeout(() => copyBtn.setAttribute("aria-label", "Copy public key"), 1500);
    });

    return el("div", { class: `key-row${isActive ? " active" : ""}` }, [valueRow, copyBtn]);
  }

  function render() {
    card.innerHTML = "";
    card.append(el("div", { class: "label" }, [el("span", {}, ["Public keys"])]));
    if (knownKeys.length === 0) {
      card.append(
        el("button", { type: "button", class: "value empty", id: "pubkey-out" }, [
          "Generate or unlock a key to see it here.",
        ]),
      );
      return;
    }
    const list = el("div", { class: "keylist" }, []);
    for (const k of knownKeys) {
      list.append(buildRow(k.publicKeyHex));
    }
    card.append(list);
  }

  setKnownPublicKey = (publicKey: Uint8Array) => rememberKey(toHex(publicKey));
  const dispose = onKeyRegistryChange(render);
  render();

  return { element: card, dispose };
}

// Right-rail API token widget - replaces the old sign-in card. Same
// rebuild-per-tab approach as buildKeysCard: state lives in lib/apiToken.ts,
// not the DOM. dispose() clears both the onApiTokenChange subscription and
// the countdown interval, or the old instance keeps ticking on a detached node.
//
// Tokens are short-lived and issued with no identity check. This widget just
// shows whether one is held, its truncated value, and a countdown.
function buildApiTokenBlock(): { element: HTMLElement; dispose: () => void } {
  const block = el("div", { class: "keyblock", id: "api-token-block" }, []);
  let countdownTimer: ReturnType<typeof setInterval> | undefined;

  function formatRemaining(expiresAt: number): string {
    const remainingSeconds = Math.max(0, Math.round(expiresAt - Date.now() / 1000));
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
  }

  function render() {
    if (countdownTimer !== undefined) clearInterval(countdownTimer);
    block.innerHTML = "";
    if (apiTokenState.token && apiTokenState.expiresAt) {
      const token = apiTokenState.token;
      const expiresAt = apiTokenState.expiresAt;
      const label = el("div", { class: "label" }, [el("span", {}, ["API token"])]);
      const copyBtn = el("button", { type: "button", class: "copy-btn", "aria-label": "Copy API token" }, [
        iconCopy(),
      ]);
      copyBtn.addEventListener("click", async () => {
        const ok = await copyToClipboard(token);
        showToast(ok ? "API token copied." : "Couldn't copy - select and copy it manually.", ok ? "success" : "error");
      });
      label.append(copyBtn);
      const valueRow = el("p", { class: "value", id: "api-token-value", title: token }, [
        `${token.slice(0, 8)}…${token.slice(-8)}`,
      ]);
      const expiryRow = el("p", { class: "value empty", id: "api-token-expiry" }, [
        `Expires in ${formatRemaining(expiresAt)}`,
      ]);
      countdownTimer = setInterval(() => {
        if (apiTokenState.expiresAt) expiryRow.textContent = `Expires in ${formatRemaining(apiTokenState.expiresAt)}`;
      }, 1000);
      const newBtn = el("button", { type: "button", class: "secondary-btn", id: "api-token-refresh" }, [
        "Request new token",
      ]);
      newBtn.addEventListener("click", () => requestToken());
      block.append(label, valueRow, expiryRow, el("div", { class: "actions" }, [newBtn]));
    } else {
      const label = el("div", { class: "label" }, [el("span", {}, ["API token"])]);
      const note = el("p", { class: "value empty", id: "api-token-note" }, [
        "No token yet - needed only to upload/download files.",
      ]);
      const requestBtn = el("button", { type: "button", class: "secondary-btn", id: "api-token-request" }, [
        "Request a token",
      ]);
      requestBtn.addEventListener("click", () => requestToken());
      block.append(label, note, el("div", { class: "actions" }, [requestBtn]));
    }
  }

  const unsubscribe = onApiTokenChange(render);
  render();

  function dispose() {
    if (countdownTimer !== undefined) clearInterval(countdownTimer);
    unsubscribe();
  }

  return { element: block, dispose };
}

function mount() {
  const app = document.querySelector<HTMLDivElement>("#app")!;
  app.innerHTML = "";
  app.className = "app";

  // Lives in the main column, pinned above the scrolling content - a fixed
  // sibling in .main-col's flex layout, not something selectTab wipes per tab.
  const seal = el("div", { class: "seal" }, ["SC"]);
  const brand = el("div", { class: "brand" }, [
    seal,
    el("div", { class: "brand-text" }, [
      el("h1", {}, ["secure-crypt"]),
      el("p", {}, ["sealed in your browser, opened only by you"]),
    ]),
  ]);
  const topBar = el("div", { class: "top-bar" }, [brand]);

  const nav = el("nav", { class: "tabs" }, []);
  const content = el("main", { class: "content", id: "tab-content" }, []);
  const mainCol = el("div", { class: "main-col" }, [topBar, content]);

  let activeTab: TabId = tabFromHash() ?? "overview";
  let disposeKeysCard: () => void = () => {};
  let disposeApiTokenBlock: () => void = () => {};
  // historyMode "push" adds a real history entry (normal nav clicks), so
  // back/forward works like it would on any other site. "replace" is only
  // for the first render, so loading /#sdk directly doesn't add a spare
  // entry. "none" is for popstate below - the entry already exists there.
  function selectTab(id: TabId, historyMode: "push" | "replace" | "none" = "push") {
    activeTab = id;
    const hash = `#${id}`;
    if (historyMode === "push" && window.location.hash !== hash) {
      history.pushState(null, "", hash);
    } else if (historyMode === "replace") {
      history.replaceState(null, "", hash);
    }
    disposeKeysCard();
    disposeApiTokenBlock();
    content.innerHTML = "";
    // Reset scroll on tab switch, otherwise the new tab inherits whatever
    // scroll offset the last one was left at instead of starting at top.
    content.scrollTop = 0;
    // .content-row holds the reading card plus the right rail (token card,
    // keys card, help card). Below 1440px it stacks normally; at >=1440px
    // style.css makes .right-rail position:fixed so it stays put while the
    // content scrolls. (Tried giving main.content its own scroll column
    // instead - reverted, it left a visible gap where the scrollbar sat.)
    const apiTokenBlock = buildApiTokenBlock();
    disposeApiTokenBlock = apiTokenBlock.dispose;
    const keysCard = buildKeysCard();
    disposeKeysCard = keysCard.dispose;
    content.append(
      el("div", { class: "content-row" }, [
        el("div", { class: "content-inner" }, [renderPanel(id)]),
        el("div", { class: "right-rail" }, [apiTokenBlock.element, keysCard.element, renderHelpRail(id)]),
      ]),
    );
    for (const btn of Array.from(nav.querySelectorAll("button[data-tab]")) as HTMLButtonElement[]) {
      btn.classList.toggle("active", btn.dataset.tab === id);
    }
    // Switching tabs re-expands any warning toast the user had shrunk but
    // not dismissed, so it doesn't stay hidden indefinitely.
    reassertStickyToasts();
  }

  for (const group of tabGroups) {
    nav.append(el("div", { class: "tab-group-label" }, [group.label]));
    for (const t of group.tabs) {
      const btn = el("button", { type: "button", "data-tab": t.id }, [
        el("span", { class: "idx", "aria-hidden": "true" }, [t.idx]),
        t.label,
      ]);
      btn.addEventListener("click", () => selectTab(t.id));
      nav.append(btn);
    }
  }

  const sidebar = el("aside", { class: "sidebar" }, [nav]);

  app.append(sidebar, mainCol);
  // Mounted alongside #app, outside the tab content, so a toast survives
  // navigating to another tab.
  document.body.append(mountToastHost());

  goToTab = selectTab;
  selectTab(activeTab, "replace");
  window.addEventListener("popstate", () => selectTab(tabFromHash() ?? "overview", "none"));

  // Test-only hook: lets a test fast-forward the token's expiry to hit the
  // "about to expire" warning without waiting out a real ~1hr token. Unused
  // outside tests.
  (window as any).__SC_TEST_SET_TOKEN_EXPIRES_AT__ = (expiresAtEpochSeconds: number) => {
    if (apiTokenState.token) setApiToken(apiTokenState.token, expiresAtEpochSeconds);
  };
}

worker
  .init()
  .then(() => {
    mount();
  })
  .catch((e) => {
    document.querySelector<HTMLDivElement>("#app")!.textContent = `Failed to initialize crypto worker: ${e}`;
  });
